@use '@/styles/tokens' as *;

// Evidence image lightbox — Figma 1158:607446: scrim 1158:608157, card 1158:611327,
// close 1158:611330, filmstrip 1158:611334 (active tile 1158:611347).
// See plan.md § Design spec for the full Figma-value → token mapping.

// `position: fixed` inside the Ticket Preview drawer's panel, NOT a portal. antd applies a
// transform to that panel only while its open/close motion classes are on, so at rest nothing
// establishes a containing block and this escapes the 464px panel to the viewport; `overflow`
// never clips a fixed descendant. Staying inside the panel is also what stops antd's focus lock
// from yanking focus back. z-index 1100 clears antd's `zIndexPopupBase` of 1000, which is where
// both stacked drawers sit. See plan.md § Layering.
.mask {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  // 142px is Figma's own top inset: it clears the floating TopNav pill (y 27 + h 56 = 83) with the
  // design's breathing room, and no --space step is near it. The other two are exact.
  padding: 142px var(--space-6) var(--space-5);
  gap: var(--space-16); // Figma 75px — 64 is the top of the scale
  background: var(--overlay-40); // Figma rgba(0,0,0,0.3), unbound — see plan.md § Accepted snaps

  // The dialog takes focus on open (tabIndex -1); it is not a control, so it shows no ring.
  &:focus-visible {
    outline: none;
  }
}

// The click-outside target, filling the scrim. Transparent — the dimming is `.mask`'s, so this is
// invisible; it exists because the click handler needs an interactive element (see the component).
.scrim {
  position: absolute;
  inset: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: default;
}

// Figma 973×484 with the image letterboxed on it. `flex: 0 1` + `min-height: 0` lets the card
// shrink on a viewport shorter than the design's 813 instead of pushing the filmstrip off-screen.
// `z-index: 1` lifts it (and the filmstrip below) clear of `.scrim`, so clicking either does not
// dismiss.
.card {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 1 484px;
  align-items: center;
  justify-content: center;
  width: min(973px, 100%);
  min-height: 0;
  background: $color-neutral-100; // Figma #F6F8F7 — nearest on the ramp
  overflow: hidden;
}

// `contain`, not `cover`: this is the image being examined, so it is never cropped.
.image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

// Figma right 12 / top 11. Geometry and focus ring come from <CloseButton tone="dark">.
.close {
  position: absolute;
  top: var(--space-3);
  inset-inline-end: var(--space-3);
}

.filmstrip {
  position: relative;
  z-index: 1; // above `.scrim`, so selecting a tile never dismisses
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--space-4); // Figma 17.25px — the same snap the Ticket Preview grid made
}

.thumb {
  flex: 0 0 auto;
  width: 92px; // Figma 92×92 — the token set has no size scale
  height: 92px;
  padding: 0;
  border: none;
  background: $color-neutral-100; // Figma #F6F8F7
  overflow: hidden;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
}

// The image being viewed (Figma 1158:611347). `box-sizing: border-box` is the global reset, so the
// 2px border is drawn inside the 92px box and marking a tile shifts nothing in the strip.
// Declared after `.thumb` on purpose: same specificity, so source order is what lets it win.
.thumbActive {
  border: 2px solid $color-success-700; // Figma Success/700 #19A974 — exact, a bound variable
}

.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
